/**
 * DialogWrapper Components
 *
 * Reusable dialog wrapper components that eliminate boilerplate for common dialog patterns.
 * Includes ConfirmDialog for confirmations and FormDialog for form-based dialogs.
 *
 * @example
 * // Simple confirmation dialog
 * <ConfirmDialog
 *   open={isOpen}
 *   onOpenChange={setIsOpen}
 *   title="Delete Item"
 *   description="Are you sure you want to delete this item?"
 *   onConfirm={handleDelete}
 *   isLoading={isDeleting}
 *   variant="destructive"
 * />
 *
 * @example
 * // Form dialog
 * <FormDialog
 *   open={isOpen}
 *   onOpenChange={setIsOpen}
 *   title="Add Goal"
 *   description="Create a new goal for this client."
 *   onSubmit={handleSubmit}
 *   isLoading={isSubmitting}
 *   isValid={form.isValid}
 *   submitLabel="Add Goal"
 *   submitIcon={Target}
 * >
 *   <FormFields />
 * </FormDialog>
 */

import { ReactNode, FormEvent } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Loader2, LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Base dialog props shared by all dialog variants
 */
interface BaseDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when open state changes */
  onOpenChange: (open: boolean) => void;
  /** Dialog title */
  title: string;
  /** Optional dialog description */
  description?: string;
  /** Title icon */
  titleIcon?: LucideIcon;
  /** Additional className for DialogContent */
  className?: string;
  /** Max width variant */
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
}

const SIZE_CLASSES = {
  sm: 'sm:max-w-[400px]',
  md: 'sm:max-w-[500px]',
  lg: 'sm:max-w-[600px]',
  xl: 'sm:max-w-[800px]',
  full: 'sm:max-w-[90vw]',
} as const;

/**
 * Props for ConfirmDialog
 */
interface ConfirmDialogProps extends BaseDialogProps {
  /** Callback when confirmed */
  onConfirm: () => void | Promise<void>;
  /** Whether the action is in progress */
  isLoading?: boolean;
  /** Button variant for confirm action */
  variant?: 'default' | 'destructive' | 'outline';
  /** Confirm button label */
  confirmLabel?: string;
  /** Cancel button label */
  cancelLabel?: string;
  /** Optional content between description and buttons */
  children?: ReactNode;
}

/**
 * Confirmation dialog for simple yes/no or confirm/cancel actions.
 *
 * @example
 * <ConfirmDialog
 *   open={isDeleteOpen}
 *   onOpenChange={setIsDeleteOpen}
 *   title="Delete Client"
 *   description="Are you sure you want to delete this client?"
 *   onConfirm={handleDelete}
 *   variant="destructive"
 *   confirmLabel="Delete"
 * />
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  titleIcon: TitleIcon,
  className,
  size = 'sm',
  onConfirm,
  isLoading = false,
  variant = 'default',
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  children,
}: ConfirmDialogProps) {
  const handleConfirm = async () => {
    await onConfirm();
    onOpenChange(false);
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className={cn(SIZE_CLASSES[size], className)}>
        <AlertDialogHeader>
          <AlertDialogTitle
            className={cn(
              'flex items-center gap-2',
              variant === 'destructive' && 'text-destructive'
            )}
          >
            {TitleIcon && <TitleIcon className="h-5 w-5" />}
            {title}
          </AlertDialogTitle>
          {description && <AlertDialogDescription>{description}</AlertDialogDescription>}
        </AlertDialogHeader>

        {children}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>{cancelLabel}</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={isLoading}
            className={cn(
              variant === 'destructive' &&
                'bg-destructive text-destructive-foreground hover:bg-destructive/90'
            )}
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/**
 * Props for FormDialog
 */
interface FormDialogProps extends BaseDialogProps {
  /** Form submit handler */
  onSubmit: (e: FormEvent) => void | Promise<void>;
  /** Whether form submission is in progress */
  isLoading?: boolean;
  /** Whether the form is valid and can be submitted */
  isValid?: boolean;
  /** Submit button label */
  submitLabel?: string;
  /** Submit button icon */
  submitIcon?: LucideIcon;
  /** Cancel button label */
  cancelLabel?: string;
  /** Loading text shown while submitting */
  loadingLabel?: string;
  /** Whether to show a cancel button */
  showCancel?: boolean;
  /** Whether to reset form on close (handled externally) */
  resetOnClose?: boolean;
  /** Callback when dialog closes (for form reset) */
  onClose?: () => void;
  /** Form content */
  children: ReactNode;
  /** Footer content (replaces default buttons) */
  footer?: ReactNode;
}

/**
 * Form dialog wrapper with standard form handling patterns.
 *
 * @example
 * <FormDialog
 *   open={isOpen}
 *   onOpenChange={setIsOpen}
 *   title="Add Goal"
 *   titleIcon={Target}
 *   description="Create a new goal for this client."
 *   onSubmit={handleSubmit}
 *   isLoading={isSubmitting}
 *   isValid={goalText.trim().length > 0}
 *   submitLabel="Add Goal"
 *   submitIcon={Target}
 * >
 *   <div className="grid gap-4 py-4">
 *     <Input value={goalText} onChange={...} />
 *   </div>
 * </FormDialog>
 */
export function FormDialog({
  open,
  onOpenChange,
  title,
  description,
  titleIcon: TitleIcon,
  className,
  size = 'md',
  onSubmit,
  isLoading = false,
  isValid = true,
  submitLabel = 'Submit',
  submitIcon: SubmitIcon,
  cancelLabel = 'Cancel',
  loadingLabel,
  showCancel = true,
  onClose,
  children,
  footer,
}: FormDialogProps) {
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen && onClose) {
      onClose();
    }
    onOpenChange(newOpen);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    await onSubmit(e);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className={cn(SIZE_CLASSES[size], className)}>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {TitleIcon && <TitleIcon className="h-5 w-5" />}
              {title}
            </DialogTitle>
            {description && <DialogDescription>{description}</DialogDescription>}
          </DialogHeader>

          {children}

          {footer ?? (
            <DialogFooter>
              {showCancel && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleOpenChange(false)}
                  disabled={isLoading}
                >
                  {cancelLabel}
                </Button>
              )}
              <Button type="submit" disabled={!isValid || isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {loadingLabel ?? submitLabel}
                  </>
                ) : (
                  <>
                    {SubmitIcon && <SubmitIcon className="mr-2 h-4 w-4" />}
                    {submitLabel}
                  </>
                )}
              </Button>
            </DialogFooter>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Props for SimpleDialog (non-form content)
 */
interface SimpleDialogProps extends BaseDialogProps {
  /** Dialog content */
  children: ReactNode;
  /** Footer content */
  footer?: ReactNode;
  /** Whether to show close button */
  showClose?: boolean;
  /** Close button label */
  closeLabel?: string;
}

/**
 * Simple dialog for displaying content without form handling.
 *
 * @example
 * <SimpleDialog
 *   open={isOpen}
 *   onOpenChange={setIsOpen}
 *   title="View Details"
 *   description="Information about this item."
 * >
 *   <div>Content here</div>
 * </SimpleDialog>
 */
export function SimpleDialog({
  open,
  onOpenChange,
  title,
  description,
  titleIcon: TitleIcon,
  className,
  size = 'md',
  children,
  footer,
  showClose = true,
  closeLabel = 'Close',
}: SimpleDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn(SIZE_CLASSES[size], className)}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {TitleIcon && <TitleIcon className="h-5 w-5" />}
            {title}
          </DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        {children}

        {(footer || showClose) && (
          <DialogFooter>
            {footer ?? (
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                {closeLabel}
              </Button>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Props for StepDialog (multi-step wizard)
 */
interface StepDialogProps extends BaseDialogProps {
  /** Current step (0-indexed) */
  currentStep: number;
  /** Total number of steps */
  totalSteps: number;
  /** Callback to go to next step */
  onNext: () => void;
  /** Callback to go to previous step */
  onPrevious: () => void;
  /** Callback when wizard completes */
  onComplete: () => void;
  /** Whether current step is valid */
  isStepValid?: boolean;
  /** Whether completing is in progress */
  isLoading?: boolean;
  /** Step content */
  children: ReactNode;
  /** Step labels for progress indicator */
  stepLabels?: string[];
}

/**
 * Multi-step wizard dialog.
 *
 * @example
 * <StepDialog
 *   open={isOpen}
 *   onOpenChange={setIsOpen}
 *   title="Setup Wizard"
 *   currentStep={step}
 *   totalSteps={3}
 *   onNext={() => setStep(s => s + 1)}
 *   onPrevious={() => setStep(s => s - 1)}
 *   onComplete={handleComplete}
 *   stepLabels={['Account', 'Profile', 'Settings']}
 * >
 *   {step === 0 && <AccountForm />}
 *   {step === 1 && <ProfileForm />}
 *   {step === 2 && <SettingsForm />}
 * </StepDialog>
 */
export function StepDialog({
  open,
  onOpenChange,
  title,
  description,
  titleIcon: TitleIcon,
  className,
  size = 'lg',
  currentStep,
  totalSteps,
  onNext,
  onPrevious,
  onComplete,
  isStepValid = true,
  isLoading = false,
  children,
  stepLabels,
}: StepDialogProps) {
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === totalSteps - 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn(SIZE_CLASSES[size], className)}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {TitleIcon && <TitleIcon className="h-5 w-5" />}
            {title}
          </DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        {/* Step indicator */}
        {totalSteps > 1 && (
          <div className="flex items-center justify-center gap-2 py-2">
            {Array.from({ length: totalSteps }).map((_, index) => (
              <div key={index} className="flex items-center gap-2">
                <div
                  className={cn(
                    'flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium',
                    index < currentStep
                      ? 'bg-primary text-primary-foreground'
                      : index === currentStep
                        ? 'border-2 border-primary text-primary'
                        : 'border-2 border-muted text-muted-foreground'
                  )}
                >
                  {index + 1}
                </div>
                {stepLabels?.[index] && (
                  <span
                    className={cn(
                      'text-sm',
                      index === currentStep ? 'font-medium' : 'text-muted-foreground'
                    )}
                  >
                    {stepLabels[index]}
                  </span>
                )}
                {index < totalSteps - 1 && (
                  <div
                    className={cn(
                      'h-0.5 w-8',
                      index < currentStep ? 'bg-primary' : 'bg-muted'
                    )}
                  />
                )}
              </div>
            ))}
          </div>
        )}

        {children}

        <DialogFooter className="flex justify-between">
          <div>
            {!isFirstStep && (
              <Button type="button" variant="outline" onClick={onPrevious} disabled={isLoading}>
                Previous
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            {isLastStep ? (
              <Button onClick={onComplete} disabled={!isStepValid || isLoading}>
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Complete
              </Button>
            ) : (
              <Button onClick={onNext} disabled={!isStepValid}>
                Next
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export type { BaseDialogProps, ConfirmDialogProps, FormDialogProps, SimpleDialogProps, StepDialogProps };
